import { appointmentIndexOptions } from '$api-client/@tanstack/react-query.gen'
import { Appointment } from '$api-client/types.gen'
import { UpcomingAppointmentCard } from '$components'
import { LinkButton, Text } from '$components'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router'
import { GenericTable } from '$components'
import { DateCell } from '$components'
import { ChipCell } from '$components'
import { StatusCell } from '$components'
import { createColumnHelper } from '@tanstack/react-table'
import { DurationCell } from '$components'
import { Router } from '$utils/router'
import { AppointmentRoutePath } from '$features/appointments/routes/protected'
import { AnimatedOutlet } from '$components'
import { NoUpcomingAppointmentsEmptyState } from '$features/pets/components/no-upcoming-appointments-empty-state'
import { NoAppointmentHistoryEmptyState } from '$features/pets/components/no-appointment-history-empty-state'
import { usePageTitle } from '$components'
import { useFilters } from '$features/filtering'
import { AppointmentsFilterParam } from '$features/appointments/enums/filter-params'
import { useSearchParams } from 'react-router-dom'

const columnHelper = createColumnHelper<Appointment>()

export function AppointmentHistoryTab() {
  usePageTitle('Appointment History')
  const { id } = useParams()
  const [searchParams] = useSearchParams()
  const { buildFilters } = useFilters()

  const { data: appointments, isLoading: isLoadingAppointments } = useQuery({
    ...appointmentIndexOptions({
      query: {
        pet: Number(id),
        per_page: 10,
      },
    }),
  })

  const columns = [
    columnHelper.accessor('start_at', {
      header: 'Date',
      cell: ({ getValue }) => <DateCell date={getValue()} />,
    }),
    columnHelper.accessor('services', {
      header: 'Services',
      cell: ({ getValue }) => {
        return <ChipCell values={getValue().map(service => service.service_values.name)} />
      },
    }),
    columnHelper.display({
      id: 'duration',
      header: 'Duration',
      cell: ({ row }) => (
        <DurationCell
          startDate={row.original.start_at}
          endDate={row.original.finish_at}
        />
      ),
    }),
    columnHelper.accessor('groomer', {
      header: 'Groomer',
      cell: ({ getValue }) => <Text type="s">{ getValue().first_name } { getValue().last_name }</Text>,
    }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: ({ getValue }) => <StatusCell status={getValue()} />,
    }),
  ]

  if (isLoadingAppointments) {
    return null
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Next Appointment Section */}
      <div className="flex flex-col gap-3">
        <Text type="h4">
          Upcoming
        </Text>

        { appointments?.data && appointments.data.length > 0 && (
          <UpcomingAppointmentCard
            data={appointments.data[0]}
          />
        )}

        { appointments?.data && appointments.data.length === 0 && (
          <NoUpcomingAppointmentsEmptyState />
        )}
      </div>

      {/* Previous Appointments Section */}
      <div className="flex flex-col gap-3">
        <div className="flex justify-between items-center">
          <Text type="h4">
            Appointments
          </Text>

          { appointments && appointments.meta.total > 5 && (
            <LinkButton
              variant="secondary"
              to={
                Router.getRoute(AppointmentRoutePath.Appointments,
                  undefined,
                  {
                    filters: buildFilters([
                      {
                        filter: {
                          id: AppointmentsFilterParam.Pet,
                          label: 'Pet',
                          type: 'combobox',
                          // @ts-expect-error Its fine to ignore here, not used for serialization
                          state: {},
                        },
                        value: id!,
                      },
                    ]),
                    [AppointmentsFilterParam.IncludeUnapproved]: searchParams.get(AppointmentsFilterParam.IncludeUnapproved) === '1' ? '1' : '0',
                  })}
              size="sm"
            >
              View All
            </LinkButton>
          )}
        </div>

        { appointments?.data && appointments.data.length > 1 && (
          <GenericTable.Container>
            <GenericTable
              data={appointments.data.slice(1)}
              columns={columns}
              rowLink={row => Router.getRoute(
                AppointmentRoutePath.AppointmentsView,
                { id: row.id },
                !row.approved_at ? { [AppointmentsFilterParam.IncludeUnapproved]: '1' } : undefined,
              )}
            />
          </GenericTable.Container>
        )}

        { appointments?.data && appointments.data.length <= 1 && (
          <NoAppointmentHistoryEmptyState />
        )}
      </div>

      <AnimatedOutlet />
    </div>
  )
}
